import GUI from 'lil-gui';
import { debugDock, makeDraggable } from './DebugDock';

/** Tuning values surfaced in the debug GUI. */
export interface DebugTuning {
  // Server-backed (sent as debug_tuning messages).
  maxSpeed: number;
  turnRate: number;
  thrustScale: number;
  stiffness: number;
  damping: number;
  enemySteerGain: number;
  collisionDamage: number;
  // Client-only.
  cameraLag: number;
  waveAmplitude: number;
  waveSpeed: number;
  grade: number;
  exposure: number;
  maxDpr: number;
  masterVolume: number;
}

export interface DebugLive {
  speed: number;
  rudder: number;
  windDirection: number;
  windStrength: number;
  sail0: string;
  sail1: string;
  /** Paired sea amplitude currently in force (physics + rendered base). */
  seaScale: number;
  /** Buoyancy decision subset, formatted for the readout. */
  probes: string;
  target: string;
  actual: string;
}

export interface DebugHandlers {
  /** A client-only value changed and the renderer/camera need updating. */
  onChange: () => void;
  /** A server-backed value changed; forward it to the authoritative room. */
  onServerTuning: (tuning: Partial<DebugTuning>) => void;
  /** The paired sea-scale slider moved: set the CPU and rendered amplitude together. */
  onSeaScale: (value: number) => void;
}

const SERVER_KEYS: Array<keyof DebugTuning> = [
  'maxSpeed',
  'turnRate',
  'thrustScale',
  'stiffness',
  'damping',
  'enemySteerGain',
  'collisionDamage',
];

/**
 * lil-gui tuning surface (enabled only with `?debug`). Server-backed controls
 * send `debug_tuning` so the authoritative simulation actually changes.
 */
export class DebugTools {
  private gui: GUI | null = null;
  private readonly live: DebugLive = {
    speed: 0,
    rudder: 0,
    windDirection: 0,
    windStrength: 0,
    sail0: '-',
    sail1: '-',
    seaScale: 1,
    probes: '-',
    target: '-',
    actual: '-',
  };

  constructor(private readonly tuning: DebugTuning, handlers: DebugHandlers) {
    const enabled = new URLSearchParams(window.location.search).has('debug');
    if (!enabled) return;

    this.gui = new GUI({ title: 'Ocean tuning' });
    // lil-gui 0.21 hides/collapses but does not drag: place it clear of the
    // minimap, make it draggable, and let the shared dock show/hide it.
    const guiRoot = this.gui.domElement;
    Object.assign(guiRoot.style, {
      top: '150px',
      left: '14px',
      right: 'auto',
      bottom: 'auto',
      zIndex: '61',
    });
    const guiTitle = guiRoot.querySelector<HTMLElement>('.lil-title');
    makeDraggable(guiRoot, guiTitle ?? guiRoot);
    debugDock.register(guiRoot);
    // Collapsed by default so the debug panels don't cover the view until asked.
    this.gui.close();

    const ship = this.gui.addFolder('Ship (server)');
    const sendServer = (key: keyof DebugTuning) => () =>
      handlers.onServerTuning({ [key]: this.tuning[key] });
    ship.add(this.tuning, 'maxSpeed', 2, 16, 0.1).onChange(sendServer('maxSpeed'));
    ship.add(this.tuning, 'turnRate', 0.1, 2.5, 0.05).onChange(sendServer('turnRate'));
    ship.add(this.tuning, 'thrustScale', 0.02, 0.4, 0.005).onChange(sendServer('thrustScale'));
    ship.add(this.tuning, 'stiffness', 4, 40, 0.5).onChange(sendServer('stiffness'));
    ship.add(this.tuning, 'damping', 1, 12, 0.1).onChange(sendServer('damping'));
    ship.add(this.tuning, 'enemySteerGain', 0, 8, 0.1).onChange(sendServer('enemySteerGain'));
    ship.add(this.tuning, 'collisionDamage', 0, 100, 1).onChange(sendServer('collisionDamage'));

    const camera = this.gui.addFolder('Camera');
    camera.add(this.tuning, 'cameraLag', 0.02, 1.2, 0.01).onChange(handlers.onChange);

    const ocean = this.gui.addFolder('Ocean');
    // One paired knob: moves the authoritative CPU amplitude and the base
    // rendered amplitude together (the sea you see == the sea the ship floats
    // on). Range matches the server `debug_sea` clamp.
    ocean
      .add(this.tuning, 'waveAmplitude', 0, 4, 0.05)
      .name('wave amplitude (paired)')
      .onChange((value: number) => handlers.onSeaScale(value));
    ocean.add(this.tuning, 'waveSpeed', 0, 3, 0.05).onChange(handlers.onChange);
    ocean.add(this.tuning, 'grade', 0, 1, 0.01).onChange(handlers.onChange);
    ocean.add(this.tuning, 'exposure', 0.4, 2, 0.01).onChange(handlers.onChange);
    ocean.add(this.tuning, 'maxDpr', 1, 2, 0.25).onChange(handlers.onChange);

    const audio = this.gui.addFolder('Audio');
    audio
      .add(this.tuning, 'masterVolume', 0, 1, 0.01)
      .name('master volume')
      .onChange(handlers.onChange);

    const live = this.gui.addFolder('Live');
    live.add(this.live, 'speed').listen();
    live.add(this.live, 'rudder').listen();
    live.add(this.live, 'windDirection').listen();
    live.add(this.live, 'windStrength').listen();
    live.add(this.live, 'sail0').listen();
    live.add(this.live, 'sail1').listen();
    live.add(this.live, 'seaScale').name('sea scale').listen();
    // The decision subset: probe heights and target vs actual DOF, so a human
    // can watch the calculation while dragging the paired slider.
    const buoyancy = live.addFolder('Buoyancy');
    buoyancy.add(this.live, 'probes').name('probes [b,s,p,st]').listen();
    buoyancy.add(this.live, 'target').name('target h/p/r').listen();
    buoyancy.add(this.live, 'actual').name('actual h/p/r').listen();
  }

  setLive(values: Partial<DebugLive>): void {
    Object.assign(this.live, values);
  }

  setBuoyancy(values: { probes: string; target: string; actual: string }): void {
    Object.assign(this.live, values);
  }

  setHidden(hidden: boolean): void {
    if (!this.gui) return;
    if (hidden) this.gui.hide();
    else this.gui.show();
  }

  dispose(): void {
    this.gui?.destroy();
    this.gui = null;
  }

  static isServerKey(key: string): boolean {
    return (SERVER_KEYS as string[]).includes(key);
  }
}
